import React from 'react';
import { Box, Chip, Paper, Typography, useTheme } from '@mui/material';
import { DisplejStylOdpovedi, Odpoved } from '../api/JsdhNotifikaceService/generated';
import { formatTime } from '../utils/utils';
import DojezdOdpocet from './DojezdOdpocet';
import { tintNestiha } from '../theme/nestihaTint';

interface OdpovedKartaProps {
  odpoved: Odpoved;
  showSpecializace?: boolean;
  displayStyle?: DisplejStylOdpovedi;
  largeMode?: boolean;
  tintCard?: boolean;
  /** ISO cas vyhlaseni poplachu — bez nej se odpocet dojezdu nevykresli. */
  casVyhlaseni?: string;
  /** Hasic podle ohlaseneho dojezdu nestihne limit vyjezdu (HO-162) — pocita volajici. */
  nestiha?: boolean;
}

const STROJNIK_COLOR = '#6a1b9a';

const OdpovedKartaStripe: React.FC<{ odpoved: Odpoved; showSpecializace: boolean; casVyhlaseni?: string; nestiha: boolean }> = ({
    odpoved,
    showSpecializace,
    casVyhlaseni,
    nestiha,
}) => {
    const theme = useTheme();
    const jeJedu = odpoved.odpoved === 'JEDU';
    const borderColor = nestiha ? theme.palette.warning.main : (jeJedu ? '#2e7d32' : '#c62828');
    const chipColor = nestiha ? 'warning' : (jeJedu ? 'success' : 'error');
    const chipLabel = jeJedu ? 'JEDU' : 'NEJEDU';

    return (
        <Paper
            data-odpoved={odpoved.odpoved}
            data-nestiha={nestiha ? 'true' : 'false'}
            elevation={2}
            sx={{
                py: 0.25,
                px: 0.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0.5,
                borderLeft: '3px solid',
                borderLeftColor: borderColor,
                // Zelena STRIPE karta podklad nema; oranzova ho dostane, jinak by zmena byla jen
                // 3px pruh, ktery na displeji pres mistnost neni videt.
                ...(nestiha && { backgroundColor: tintNestiha(theme, 0.13) }),
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: 'var(--dashboard-card-jmeno, 0.75rem)', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {odpoved.jmeno}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: 'var(--dashboard-card-meta, 0.65rem)', lineHeight: 1 }}>
                        {formatTime(odpoved.cas)}
                    </Typography>
                    {showSpecializace && odpoved.specializace?.map((s) => (
                        <Chip
                            key={`spec-${s.id ?? s.kod}`}
                            label={s.kod}
                            size="small"
                            variant="outlined"
                            color="secondary"
                            sx={{ fontSize: '0.5rem', height: 14, '& .MuiChip-label': { px: 0.5 } }}
                        />
                    ))}
                    {odpoved.dojezdCas && casVyhlaseni && (
                        <DojezdOdpocet
                            dojezdCas={odpoved.dojezdCas}
                            casVyhlaseni={casVyhlaseni}
                            fontSize="var(--dashboard-card-meta, 0.65rem)"
                        />
                    )}
                </Box>
            </Box>
            <Chip
                label={chipLabel}
                color={chipColor}
                size="small"
                sx={{ fontSize: '0.55rem', height: 16, '& .MuiChip-label': { px: 0.75 } }}
            />
        </Paper>
    );
};

const OdpovedKarta: React.FC<OdpovedKartaProps> = ({
    odpoved,
    showSpecializace = false,
    displayStyle = 'STRIPE',
    largeMode = false,
    tintCard = false,
    casVyhlaseni,
    nestiha = false,
}) => {
    const theme = useTheme();

    if (displayStyle === 'STRIPE') {
        return <OdpovedKartaStripe odpoved={odpoved} showSpecializace={showSpecializace} casVyhlaseni={casVyhlaseni} nestiha={nestiha} />;
    }

    const jeJedu = odpoved.odpoved === 'JEDU';
    const stripeColor = nestiha ? theme.palette.warning.main : (jeJedu ? theme.palette.success.main : theme.palette.error.main);

    const tintBg = nestiha
        ? tintNestiha(theme, 0.13)
        : tintCard
            ? (jeJedu
                ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.22)' : 'rgba(46, 125, 50, 0.13)')
                : (theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.22)' : 'rgba(198, 40, 40, 0.12)'))
            : 'transparent';

    const fnChips = (odpoved.funkce ?? [])
        .filter((f) => f.kod === 'VD' || f.kod === 'S')
        .map((f) => {
            const isVD = f.kod === 'VD';
            const bg = isVD ? theme.palette.info.main : STROJNIK_COLOR;
            return (
                <Chip
                    key={`fn-${f.id ?? f.kod}`}
                    label={f.kod}
                    size="small"
                    sx={{
                        height: 'auto',
                        py: 0.25,
                        fontSize: 'var(--dashboard-tinted-chip-fontsize, 0.85rem)',
                        fontWeight: 800,
                        letterSpacing: '0.5px',
                        backgroundColor: bg,
                        color: theme.palette.getContrastText(bg),
                        '& .MuiChip-label': { px: 0.75 },
                    }}
                />
            );
        });

    const specChips = showSpecializace
        ? (odpoved.specializace ?? []).map((s) => (
            <Chip
                key={`spec-${s.id ?? s.kod}`}
                label={s.kod}
                size="small"
                variant="outlined"
                sx={{
                    height: 'auto',
                    py: 0.25,
                    fontSize: 'var(--dashboard-tinted-chip-fontsize, 0.85rem)',
                    fontWeight: 800,
                    letterSpacing: '0.5px',
                    color: theme.palette.warning.dark,
                    borderColor: theme.palette.warning.light,
                    backgroundColor: theme.palette.background.paper,
                    '& .MuiChip-label': { px: 0.75 },
                }}
            />
        ))
        : [];

    const allChips = [...fnChips, ...specChips];
    const hasChips = allChips.length > 0;

    const nameFontVar = hasChips
        ? 'var(--dashboard-tinted-card-jmeno, 1.6rem)'
        : 'var(--dashboard-tinted-card-jmeno-no-chips, 2rem)';
    const metaFontVar = 'var(--dashboard-tinted-card-meta, 1rem)';

    return (
        <Box
            data-odpoved={odpoved.odpoved}
            data-no-chips={hasChips ? 'false' : 'true'}
            data-nestiha={nestiha ? 'true' : 'false'}
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                alignItems: 'center',
                columnGap: 1,
                px: 1,
                py: 0.5,
                backgroundColor: tintBg,
                borderRadius: 0.5,
                borderLeft: 'var(--dashboard-card-stripe-width, 4px) solid',
                borderLeftColor: stripeColor,
                minWidth: 0,
                minHeight: 'var(--dashboard-tinted-card-row-height, 60px)',
            }}
        >
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography
                    component="span"
                    sx={{
                        fontWeight: 700,
                        fontSize: nameFontVar,
                        lineHeight: 1.15,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {odpoved.jmeno}
                </Typography>
                {hasChips && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        {allChips}
                    </Box>
                )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                <Typography
                    component="span"
                    sx={{
                        fontSize: metaFontVar,
                        fontWeight: 700,
                        color: 'text.secondary',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {formatTime(odpoved.cas)}
                </Typography>
                {odpoved.dojezdCas && casVyhlaseni && (
                    <DojezdOdpocet
                        dojezdCas={odpoved.dojezdCas}
                        casVyhlaseni={casVyhlaseni}
                        fontSize={metaFontVar}
                    />
                )}
            </Box>
        </Box>
    );
};

export default OdpovedKarta;
